<script>
import {useMenuStore} from "@js/store/MenuStore.js";
import IconAngleLeft from "@js/components/Icons/IconAngleLeft.vue";
import {Link, router} from "@inertiajs/vue3";
import {onBeforeUnmount, ref, watch} from "vue";

export default {
    name: "TopBarCatalogDropdown",
    components: {IconAngleLeft, Link},
    methods: {
        buildMenuLink(item) {
            if (item?.url) {
                return item.url;
            }

            if (!item?.route_name) {
                return "#";
            }

            const fullParams = item.route_params ?? {};
            const { query, ...pureRouteParams } = fullParams;

            let url = this.route(item.route_name, pureRouteParams);

            if (query && typeof query === "object") {
                const queryParams = new URLSearchParams();

                for (const [key, values] of Object.entries(query)) {
                    queryParams.append(key, JSON.stringify(values));
                }

                const queryString = queryParams.toString();
                if (queryString) {
                    url += `?${queryString}`;
                }
            }

            return url;
        }
    },
    setup() {
        const menuStore = useMenuStore();
        const menuRef = ref(null);

        const handleClickOutside = (event) => {
            if (menuStore.isMenuCatalogOpen && menuRef.value && !menuRef.value.contains(event.target)) {
                menuStore.closeMenuCatalog();
            }
        };

        watch(
            () => menuStore.isMenuCatalogOpen,
            (isOpen) => {
                if (isOpen) {
                    setTimeout(() => {
                        document.addEventListener("click", handleClickOutside);
                    }, 50);
                } else {
                    document.removeEventListener("click", handleClickOutside);
                }
            }
        );

        onBeforeUnmount(() => {
            document.removeEventListener("click", handleClickOutside);
            menuStore.closeMenuCatalog();
        });

        router.on("before", () => {
            menuStore.closeMenuCatalog();
        });

        return {menuStore, menuRef};
    },
};
</script>

<template>
    <nav
        v-show="menuStore.isMenuCatalogOpen"
        ref="menuRef"
        class="absolute z-20 inset-x-0 bg-white border-y border-gray-200 shadow-xs"
    >
        <div class="container">
            <div class="flex w-full min-h-[520px]">
                <!-- Левая колонка -->
                <div class="w-[320px] shrink-0 border-r border-gray-200 bg-white">
                    <Link
                        v-for="(category, key) in menuStore.menuItems"
                        :key="key"
                        :href="buildMenuLink(category)"
                        @mouseenter="menuStore.changeActiveCatalogMenu(key)"
                        class="block"
                    >
                        <div
                            class="flex items-center justify-between gap-3 px-4 py-4 transition"
                            :class="menuStore.activeCatalog === key ? 'bg-grayBg' : 'md:hover:bg-grayBg'"
                        >
                            <div class="flex items-center gap-3 min-w-0">
                                <img
                                    v-if="category.icon"
                                    :src="category.icon"
                                    :alt="category.title"
                                    class="h-9 w-9 rounded object-cover shrink-0"
                                />
                                <div class="truncate text-[15px] font-medium text-gray-900">
                                    {{ category.title }}
                                </div>
                            </div>

                            <IconAngleLeft class="rotate-180 shrink-0 text-gray-500"/>
                        </div>
                    </Link>
                </div>

                <!-- Правая колонка -->
                <div class="flex-1 bg-white px-6 py-5">
                    <template v-if="menuStore.activeCategory">
                        <!-- Если у категории есть группы -->
                        <template v-if="menuStore.activeCategory.sub_menu?.length">
                            <div
                                class="grid gap-x-8 gap-y-8"
                                :class="[
                                    menuStore.activeCategory.sub_menu.length >= 4
                                        ? 'grid-cols-4'
                                        : menuStore.activeCategory.sub_menu.length === 3
                                            ? 'grid-cols-3'
                                            : menuStore.activeCategory.sub_menu.length === 2
                                                ? 'grid-cols-2'
                                                : 'grid-cols-1'
                                ]"
                            >
                                <div
                                    v-for="(group, groupIndex) in menuStore.activeCategory.sub_menu"
                                    :key="groupIndex"
                                    class="min-w-0"
                                >
                                    <div class="border-b border-gray-200 pb-3 mb-3">
                                        <Link
                                            :href="buildMenuLink(group)"
                                            class="text-[18px] font-semibold text-gray-900 md:hover:text-navHoverColor transition"
                                        >
                                            {{ group.title }}
                                        </Link>
                                    </div>

                                    <div
                                        v-if="group.sub_menu?.length"
                                        class="flex flex-col gap-2"
                                    >
                                        <Link
                                            v-for="(item, itemIndex) in group.sub_menu"
                                            :key="itemIndex"
                                            :href="buildMenuLink(item)"
                                            class="block text-sm text-gray-800 md:hover:text-navHoverColor transition leading-snug"
                                        >
                                            {{ item.title }}
                                        </Link>
                                    </div>

                                    <div
                                        v-else
                                        class="text-sm text-gray-400"
                                    >
                                        Нет товаров
                                    </div>
                                </div>
                            </div>
                        </template>

                        <!-- Если вообще пусто -->
                        <template v-else>
                            <div class="h-full flex items-center justify-center text-gray-400 text-sm">
                                В этой категории пока нет товаров
                            </div>
                        </template>
                    </template>
                </div>
            </div>
        </div>
    </nav>
</template>

<style scoped>
</style>
